import { useApolloClient } from '@apollo/client/react';
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import { useAdminLogin, useAdminLogout, useAdminMe } from '../hooks/use-admin';

export type AdminUserRole = 'admin' | 'moderator' | 'viewer';

interface AdminUser {
	id: string;
	email: string;
	role: AdminUserRole;
}

interface AdminAuthContextValue {
	user: AdminUser | null;
	loading: boolean;
	isAdmin: boolean;
	isManager: boolean;
	isViewer: boolean;
	login: (email: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
	const [user, setUser] = useState<AdminUser | null>(null);
	const { data, loading: meLoading, refetch } = useAdminMe();
	const [loginMutation] = useAdminLogin();
	const [logoutMutation] = useAdminLogout();
	const apolloClient = useApolloClient();

	const ALLOWED_ROLES: AdminUserRole[] = ['admin', 'moderator', 'viewer'];

	useEffect(() => {
		if (!meLoading) {
			const me = data?.me;
			const role = me?.role as AdminUserRole | undefined;
			setUser(
				me && role && ALLOWED_ROLES.includes(role)
					? { ...me, role }
					: null,
			);
		}
	}, [data, meLoading]);

	const login = useCallback(
		async (email: string, password: string) => {
			const result = await loginMutation({
				variables: { input: { email, password } },
			});
			if (!result.data?.login.success) {
				throw new Error('Login failed');
			}
			await apolloClient.resetStore();
			const meResult = await refetch();
			const me = meResult.data?.me;
			const role = me?.role as AdminUserRole | undefined;
			if (!me || !role || !ALLOWED_ROLES.includes(role)) {
				throw new Error('Not authorized');
			}
			setUser({ ...me, role });
		},
		[loginMutation, apolloClient, refetch],
	);

	const logout = useCallback(async () => {
		await logoutMutation();
		await apolloClient.resetStore();
		setUser(null);
	}, [logoutMutation, apolloClient]);

	const isAdmin = user?.role === 'admin';
	const isManager = user?.role === 'moderator';
	const isViewer = user?.role === 'viewer';

	return (
		<AdminAuthContext.Provider
			value={{
				user,
				loading: meLoading,
				isAdmin,
				isManager,
				isViewer,
				login,
				logout,
			}}
		>
			{children}
		</AdminAuthContext.Provider>
	);
}

export function useAdminAuth() {
	const ctx = useContext(AdminAuthContext);
	if (!ctx)
		throw new Error('useAdminAuth must be used within AdminAuthProvider');
	return ctx;
}
